<template>
  <div class="scene">

    <div id="three-scene-canvas"></div>

    <p v-if="isDebug" class="debug right">
      {{ ( interaction && interaction.target && interaction.target.index ) ? '&#128433;&nbsp;'+interaction.target.index : '' }}
      {{ ( interaction && interaction.active ) ? '&#128070;&nbsp;'+interaction.active.index : '' }}
    </p>
    
    <p v-if="isDebug" class="debug left">
      {{ ( renderer && renderer.info.render ) ? '⏱️&nbsp;' + Math.round( renderer.info.render.frame/this.fps) : '' }}<br>
      {{ ( renderer && renderer.info.render ) ? '🌐&nbsp;' + renderer.info.render.triangles : '' }}<br>
      {{ ( renderer && renderer.info.render ) ? '✏️&nbsp;'+renderer.info.render.calls : '' }}<br>
      {{ ( renderer && renderer.info.memory ) ? '📏&nbsp;'+renderer.info.memory.geometries : '' }}<br>
      {{ ( renderer && renderer.info.memory ) ? '🖼️&nbsp;'+renderer.info.memory.textures : '' }}<br>
      {{ ( stage && stage.segments ) ? '🛣&nbsp;'+stage.segments.length : '' }}<br>
      {{ ( stage && stage.bricks ) ? '🧱&nbsp;'+stage.bricks : '' }}<br>
      {{ ( stage && stage.props ) ? '🌳&nbsp;'+stage.props : '' }}
    </p>

  </div>
</template>

<script>
  import {
    Scene,
    PerspectiveCamera,
    WebGLRenderer,
    AmbientLight,
    HemisphereLight,
    AxesHelper,
    Vector2,
    Vector3,
    sRGBEncoding,
    PCFSoftShadowMap,
    Raycaster,
    Clock,
  } from 'three'

  // import * as TWEEN from "@tweenjs/tween.js"; /* TODO: come back to this - https://dev.to/pahund/animating-camera-movement-in-three-js-17e9 */

  import Stats from 'stats.js'
  import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
  import { loadSounds, loadJsons, loadFonts, loadTextures, loadImages } from "../assets/js/loaders.js";
  import { Scenery } from '../assets/js/scenery'
  import { Stage } from '../assets/js/stage'

  import assets from '../assets/json/assets.json';
  import config from '../assets/json/config.json'

  export default {
    name: 'Scene',

    data () {
      return {
        
        canvas: null,
        scene: null,
        stage: null,
        scenery: null,
        camera: null,
        renderer: null,
        controls: null,
        raycaster : null,

        interaction: {
          target : null,
          active : null,
          scrolling : null,
          position : new Vector2(),
          lastPosition : new Vector2(),
          touchPosition  : new Vector2(),
        },

        actions:  {
          zoom : false,
          zoomed : false,
          up : false,
          down : false,
        },

        stats :  null,
        clock : new Clock(),
        delta : 0,
        fps : 60,
        needsUpdate : true,
        startZoomIn : false,
        startOn : null,
        requestId : null

      }
    },

    watch: {
      actions: {
        handler: function() {
          this.$emit("actionsChanged", this.actions);
        },
        deep: true
      },
      isVisible:{
        handler: function(boo){
          if( boo ){
            setTimeout(()=>{
              this.startZoomIn = true;
               this.zoomToDeepLink();
            }, (window.innerWidth > 1024 ) ? 1000 : 2000 );
          }
        }
      }
    },

    props: {
      handleSceneClick: {
        default: null,
        type: Function
      },
      isPaused: {
        default: true,
        type: Boolean
      },
      isVisible: {
        default: false,
        type: Boolean
      }
    },

    computed: {
      isDebug(){
        return ( window.location.host.includes('eltonjohn.com') ) ? false : config.debug; //( window.CONFIG && window.CONFIG.debug === true ) ? true : false;
      },
      width(){
        // console.log( 'WIDTH', window.innerWidth, this.canvas.offsetWidth );
        return ( this.canvas ) ? this.canvas.offsetWidth : window.innerWidth;
      },
      height(){
        // console.log( 'HEIGHT', window.innerHeight, window.innerHeight - 75, this.canvas.offsetHeight );
        return ( this.canvas ) ? this.canvas.offsetHeight : (window.innerWidth < 600 ) ? window.innerHeight - 75 : window.innerHeight
        // return window.innerWidth < 600 ? window.innerHeight - 75 : window.innerHeight;
      }
    },

    async created(){

      // console.log('Scene Created');

      /* CONFIG */
      window.CONFIG = await config;
      if( config.controls.mouse && !matchMedia('(pointer:fine)').matches ) config.controls.mouse = false // DISABLE AS MOUSE NOT FOUND! 
      if( config.controls.touch && !('ontouchstart' in document.documentElement) ) config.controls.touch = false // DISABLE AS NO TOUCH EVENTS NOT FOUND! 
      if( config.controls.keyboard && config.controls.touch && !config.controls.mouse ) config.controls.keyboard = false // DISABLE AS PROBABLY ON TOUCH DEVICE!
      window.CONFIG.road.limit = ( !isNaN( process.env.VUE_APP_BRICKS_LIMIT) ) ? parseInt( process.env.VUE_APP_BRICKS_LIMIT ) : window.CONFIG.road.limit; // CAN OVERWRITE LIMIT

      /* ASSETS */
      window.ASSETS = await this.preloadAssets( assets );

      /* DATA */
      const hash = ( window.location.hash ) ? window.location.hash.substring(1) : null;
      const limit = config.road.limit || 99;
      window.DATA = await this.fetchData( limit, hash );
      this.startOn = window.DATA.findIndex(obj => { return obj.uid === hash });
      if( this.startOn < 1 ) this.startOn = 1;//config.brick.start;

      /* BUILD */
      this.init();
      if( !this.canvas ) return null;
      this.initDebug();
      this.addListeners();
      
      /* START */
      this.stage.moveTo( this.startOn ); 

      setTimeout(()=>{
        this.animate();
        this.applyGravity();
        this.camera.fov += 10;
        this.camera.updateProjectionMatrix();
        this.needsUpdate = true;
        this.$emit("isReady", true);
      }, 150 );

      if( hash ){

        // setTimeout(()=>{

        //   let targets = this.stage.activeObjects('targets');
        //   console.log( targets );
        //   targets = targets.filter(actor => actor.data && actor.data.uid == hash );
        //   console.log( targets, hash, targets[0] );
        //   if( targets.length > 0 ){
        //     this.interaction.active = targets[0];
        //     this.interaction.active.onSelect();
        //     this.zoomCamera( this.startOn );
        //     this.$emit( 'event' , {
        //       name : this.interaction.active.name,
        //       type : ( this.interaction.active.name == 'brick' ) ? 'select' : 'click',
        //       ...( this.interaction.active.data && { data : this.interaction.active.data  } ) 
        //     });
        //   }
        // }, 4000 );

      }
    },

    

    beforeDestroy() {
      const el = document.querySelector('#three-scene-canvas');
      el.parentNode.removeChild(el);
    },

    destroyed(){

      console.log('Scene Destroyed', 'refresh needed');

      // https://stackoverflow.com/questions/20997669/memory-leak-in-three-js

      /* TODO:
        * - do we actually need this?
      */

      cancelAnimationFrame( this.requestId );

      if(!this.renderer) return null;
     
      this.scene.traverse((object)=>{
        if( object.isMesh ){
          if(object.geometry.dispose)object.geometry.dispose();
          if(object.material.dispose)object.material.dispose();
          if(object.dispose)object.dispose();
          this.scene.remove( object );
        }
      });

      this.renderer.domElement.addEventListener('dblclick', null, false); //remove listener to render
      this.scene = null;
      this.projector = null;
      this.camera = null;
      this.controls = null;

      this.renderer.forceContextLoss();
      this.renderer.domElement = null;
      this.renderer.dispose();
      this.renderer = null;

    },

    

    methods: {

      async preloadAssets( assets ) { 
        assets.forEach(asset => { /* Get local assets too */
          if ( asset.filename ) asset.file = require( `@/assets/1024/${ asset.filename }` );
        });
        const loadedAssets = { /* what do we want to load? */
          sounds : false,
          images : true,
          textures : true,
          fonts : false,
          json : false,
        }
        if(loadedAssets.sounds)loadedAssets.sounds = await loadSounds(  assets.filter(obj => { return obj.type === 'sound' }) );
        if(loadedAssets.images)loadedAssets.images = await loadImages(  assets.filter(obj => { return obj.type === 'image' }) );
        if(loadedAssets.textures)loadedAssets.textures = await loadTextures(  assets.filter(obj => { return obj.type === 'texture' }) );
        if(loadedAssets.fonts)loadedAssets.fonts = await loadFonts(  assets.filter(obj => { return obj.type === 'font' }) );
        if(loadedAssets.json) loadedAssets.jsons = await loadJsons(  assets.filter(obj => { return obj.type === 'json' }) );
        return loadedAssets;
      },

      async fetchData( limit = 1000, uid = null ) { 
        let path, response, data;
        try {
          path = ( uid ) ? '/submissions/' + uid + '?limit=' + limit : '/submissions?limit=' + limit;
          response = await fetch(`${process.env.VUE_APP_API_URL}` + path);
          data = await response.json();
          return data;
        } catch (error) {
          if( !window.location.host.includes('eltonjohn.com') ){ // USE FALLBACK DATA LOCALLY SO WE DON'T NEED TO RUN API!!
            response = await fetch('/MOCK_DATA.json');
            data = await response.json();
            return data;
          }else{
            console.log(error);
          }
          return null;
        }
      },

      init(){

        this.canvas = document.getElementById('three-scene-canvas');
        if(!this.canvas) return null;
        this.scene = new Scene();

        /*
        let FOV
        let FAR
        let NEAR = 40

        // Mobile camera
        if (window.innerWidth <= 768) {
          FOV = 50
          FAR = 1200
          // 769px - 1080px screen width camera
        } else if (window.innerWidth >= 769 && window.innerWidth <= 1080) {
          FOV = 50
          FAR = 1475
          // > 1080px screen width res camera
        } else {
          FOV = 40
          FAR = 1800
        }
        */

        /* TODO: move above respsonsove settings in config */

        this.camera = new PerspectiveCamera(
          config.camera.fov,
          this.canvas.getBoundingClientRect().width / this.canvas.getBoundingClientRect().height,
          config.camera.near,
          config.camera.far
        )

        if ( this.width < 1024 && this.height < 539){
          this.camera.position.set(config.cameraLandscapeMobile.x, config.cameraLandscapeMobile.y, config.cameraLandscapeMobile.z);
        } else {
          this.camera.position.set(config.camera.x, config.camera.y, config.camera.z);
        }
        
        
        this.renderer = new WebGLRenderer({
          antialias: ( window.devicePixelRatio > 1 ) ? false : true,
          powerPreference: "high-performance"
        });

        this.renderer.outputEncoding = sRGBEncoding
        this.renderer.gammaFactor = 2.2;

        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.enablePan = config.controls.pan || false;
        this.controls.enableRotate = config.controls.rotate || false;
        this.controls.enableZoom = config.controls.zoom || false;
        this.controls.addEventListener('change', ()=>{
          this.needUpdate = true;
        }) // can use this instead of request aniamtion frame
        this.controls.saveState();

        this.renderer.setSize(this.canvas.offsetWidth, this.canvas.offsetHeight);
        if( config.lights.enable ) this.renderer.setClearColor("#212121");
        if( !config.lights.enable ) this.renderer.setClearColor(0, 1); // white if using basic materials
        this.renderer.shadowMap.enabled = false;
        this.renderer.shadowMap.type = PCFSoftShadowMap;
        this.renderer.shadowMapSoft = true;
        this.renderer.shadowMap.autoUpdate = false;
        this.renderer.shadowMap.needsUpdate = false;
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.canvas.append(this.renderer.domElement);

        // LIGHTING
        if( config.lights.enable ){
          const ambientLight = new AmbientLight ( config.lights.ambient, config.lights.intensity );
          this.scene.add( ambientLight );
          const hemispherelight = new HemisphereLight( config.lights.sky, config.lights.ground, config.lights.intensity ); // sky, ground, intensity
          this.scene.add( hemispherelight );
        }

        // RAYCASTER
        this.raycaster = new Raycaster();

        // OBJECTS
        this.scenery = new Scenery( this.camera );
        this.scene.add(this.scenery);
        this.stage = new Stage( window.DATA );
        this.scene.add( this.stage );

      },

      initDebug(){

        if( this.isDebug !== true ) return null;

        const axesHelper = new AxesHelper( 500 );
        axesHelper.position.y = 10;
        this.scene.add( axesHelper ); 

        this.stats = new Stats();
        this.stats.showPanel( 2 ); // 0: fps, 1: ms, 2: mb, 3+: custom
        document.body.appendChild( this.stats.dom );
      },

      addListeners(){

        window.addEventListener("resize", this.resizeCanvas);

        const interaction = ( config.controls.touch && !config.controls.mouse ) ? 'touchend' : 'click';
        window.addEventListener(interaction,this.onInteraction);

        if( config.controls.mouse ){
          window.addEventListener("mousemove", (event)=>{
            const point = new Vector2( (event.clientX / this.width) * 2 - 1, -(event.clientY / this.height) * 2 + 1 );
            this.interaction.position = point;
          });
        }

        if( config.controls.keyboard ){
          document.addEventListener( 'keydown', this.onKey );
          document.addEventListener( 'keyup', this.onKey );
        }

    
        if( config.controls.mouse ){
          window.addEventListener("wheel",
            (evt) => {

              if(this.interaction.scrolling) return null;
              // const deltaY = event.deltaMode === 1 ? -evt.deltaY * 18 : -evt.deltaY;
              const direction = ( evt.deltaY < 0 ) ? 'up' : 'down';
              this.updateActions( direction, true  );
              clearTimeout(this.interaction.scrolling);
              this.interaction.scrolling = setTimeout(() =>{
                this.interaction.scrolling = null;
                this.updateActions( direction, false  );
              }, 250);

            },
            { passive: true }
          );
        }

        this.$on('event', ( mesh )=>{

          /* LOCATION HASH LOGIC WHEN SELECTING AND DESELECTING A BRICK */
          if( mesh.name == 'brick' && mesh.type == 'select' ){
            window.location.hash = mesh.data.uid;  
          }else if( mesh.name == 'brick' && mesh.type == 'deselect' ){
            var uri = window.location.toString();
            if (uri.indexOf("#") > 0) {
              var clean_uri = uri.substring(0, uri.indexOf("#"));
              window.history.replaceState({}, document.title, clean_uri);
            }
          }
        });

      },

      onInteraction(evt){

        if( this.isPaused || evt.target.tagName.toLowerCase() != 'canvas' ) return null;
        
        let clicked = null;

        if( evt.type != 'click' ){

          evt = (typeof evt.originalEvent === 'undefined') ? evt : evt.originalEvent;

          const touch = evt.touches[0] || evt.changedTouches[0];
          this.interaction.touchPosition.set( (touch.pageX / this.width) * 2 - 1, -(touch.pageY / this.height) * 2 + 1  );
          clicked = this.checkIntersects(  this.interaction.touchPosition );
        }else if( this.interaction.target ){ // MOUSE IS OVER THE TARGET ALREADY!
          clicked = this.interaction.target;
        }

        if( clicked ) {

          if( this.interaction.active && this.interaction.active.onDeselect ) {
            this.interaction.active.onDeselect(); // current active target
            this.$emit( 'event' , { // brick or prop for now
              name : this.interaction.active.name,
              type : 'deselect',
            });
          }

          if( clicked.onSelect ){

            this.interaction.active = clicked;
            this.interaction.active.onSelect();

            if( this.actions.zoomed ){
              this.zoomCamera( clicked.index );
            }else{
              this.actions.zoom = true;
            }
            
          }

          if( clicked.name != 'brick' ){

            this.handleSceneClick({
              name : clicked.name,
              type : ( clicked.name == 'brick' ) ? 'select' : 'click',
              ...( clicked.data && { data : clicked.data  } ) 
            })

          }else{

            setTimeout( ()=>{
              this.$emit( 'event' , {
                name : clicked.name,
                type : ( clicked.name == 'brick' ) ? 'select' : 'click',
                ...( clicked.data && { data : clicked.data  } ) 
              });
            }, 500 );

          }
          
          this.needsUpdate = true;

        } 


      },

  
      onKey( evt ){

        if(this.isPaused) return null;

        evt.preventDefault();

        switch ( evt.code ) {
          case 'KeyW':
            this.updateActions( 'up', ( evt.type == 'keydown' ) ? true : false  );
            // this.actions.up = ( evt.type == 'keydown' ) ? true : false;
            break;
          case 'KeyS':
            this.updateActions( 'down', ( evt.type == 'keydown' ) ? true : false  );
            // this.actions.down  = ( evt.type == 'keydown' ) ? true : false;
            break;

          case 'Space':
            // if( evt.type == 'keyup' ) this.actions.zoom = !this.actions.zoom;
            if( evt.type == 'keyup' ) this.updateActions( 'zoom', this.actions.zoom = !this.actions.zoom );
            break;
        }

      },

      updateActions( type, boo ){
        this.actions[type] = boo;
      },

      zoomToDeepLink(){

        const hash = ( window.location.hash ) ? window.location.hash.substring(1) : null;
        if(! hash ) return null;

        let targets = this.stage.activeObjects('targets');
        console.log( targets );
        targets = targets.filter(actor => actor.data && actor.data.uid == hash );
        console.log( targets, hash, targets[0] );
        if( targets.length > 0 ){
          this.interaction.active = targets[0];
          this.interaction.active.onSelect();

          setTimeout(()=>{

            this.zoomCamera( this.startOn );
            this.actions.zoom = true;
            this.$emit( 'event' , {
              name : this.interaction.active.name,
              type : ( this.interaction.active.name == 'brick' ) ? 'select' : 'click',
              ...( this.interaction.active.data && { data : this.interaction.active.data  } ) 
            });

          }, 1500 );

        }
      },

      zoomCamera( brick = null ){   // https://stackoverflow.com/questions/64168783/how-do-you-move-a-camera-directly-above-an-object-in-3d-space
        //console.log('zoom in scene');
        let mobileZoomPortrait = window.innerHeight / 70.8;
        let mobileZoomLandscape = 1;
        let amount = 6;
        if(window.innerWidth < 1024 && window.innerHeight > 539){
          amount = mobileZoomPortrait;
        } else if (window.innerWidth < 1024 && window.innerHeight < 539){
          amount = mobileZoomLandscape;
        } else {
          amount = 6;
        }

        //alert( 'screen size' + window.innerWidth + 'x'+ window.innerHeight + ', pixel ratio ' + window.devicePixelRatio + ', zoom: ' + mobileZoom );
        let offset = new Vector3( 0, config.brick.voxel*amount, 0 );
        let target = new Vector3( 0, 0, 0 );
        this.camera.position.addVectors( target, offset );
        this.camera.lookAt( target );
        if ( brick ) {
          this.stage.moveTo( brick );
          this.applyGravity();
          setTimeout(()=>{ // extra gravity a little later never hurt anybody
            this.applyGravity();
          }, 1000/this.fps );
        }

        // const actors = this.stage.activeObjects('actors');
        // actors.forEach( actor => {
        //   if(!actor.visible) return;
        //   const point = new Vector3();
        //   actor.getWorldPosition( point );
        //   let distance = this.scenery.floorcloth.intersectTerrain( point );
        //   distance = ( !Number.isNaN(distance) ) ? distance : 0;
        //   if( actor.name == 'row' ) distance += config.gravity.offset;
        //   actor.position.y -= distance;
        //   if( actor.name != 'row' ) actor.rotation.y = Math.atan2( ( this.camera.position.x - actor.position.x ), ( this.camera.position.z - actor.position.z ) );
        // });

      },

      applyGravity(){ // AND ROTATION

        if( !config.gravity.enable ) return null;
        
        //config.gravity.enable = false; // HACK: simple throttle
        
        /* TODO
          * - rotate bricks with slope
          * - move code into stage or segments?
          */

        const actors = this.stage.activeObjects('actors');
        actors.forEach( actor => {
          if(!actor.visible) return;
          const point = new Vector3();
          actor.getWorldPosition( point );
          let distance = this.scenery.floorcloth.intersectTerrain( point );
          distance = ( !Number.isNaN(distance) ) ? distance : 0;
          if( actor.name == 'row' ) distance += config.gravity.offset;
          actor.position.y -= distance;
          if( actor.name != 'row' ) { // PROPS ONLY
            actor.rotation.y = Math.atan2( ( this.camera.position.x - actor.position.x ), ( this.camera.position.z - actor.position.z ) );
          }
        });

        this.needsUpdate = true;

        // setTimeout(()=>{
        //   config.gravity.enable = true; // HACK: simple throttle
        // }, 1000/this.fps );

      },


      checkIntersects( point ){

        /* TODO
          * - better throtte check like https://stackoverflow.com/questions/48542625/bad-three-js-performance-with-raycasters
          * - move code into stage or segments?
          */

        this.interaction.lastPosition.copy( point );
        this.raycaster.setFromCamera( point, this.camera );

        // console.log( this.stage.segments[ this.stage.currentSegment ].targets.length );
        const targets = this.stage.activeObjects('targets');
        const intersects = this.raycaster.intersectObjects( targets ); // stage children should be segments of bricks
        //const intersects = this.raycaster.intersectObjects( this.stage.bricks ); // stage children should be segments of bricks

        return (intersects.length > 0  ) ? intersects[0].object : null;
      },

      animate() {
        this.requestId = requestAnimationFrame( this.animate );
        this.delta += this.clock.getDelta();
        if( this.delta > 1 / this.fps ){
          if(this.isDebug) this.stats.begin();
          this.loop();
          this.render();
          this.delta = this.delta % (1 /this.fps) ;
          if(this.isDebug) this.stats.end();
        }
      },

      loop(){

        if( this.isPaused ) return null; /* TODO: use this for pause/resume */

        /* ZOOM IN THE CAMERA A LITTLE ON FIRST SHOW */
        if( this.isVisible && this.startZoomIn && this.camera.fov  > config.camera.fov ){
          this.camera.fov -= 0.25;
          if( this.camera.fov < config.camera.fov ) this.camera.fov = config.camera.fov;
          this.camera.updateProjectionMatrix();
          this.needsUpdate = true;
        }

        /* MOVE BRICK IF MOUSE OVER IT */

        if( config.controls.mouse && this.interaction.position.distanceTo(this.interaction.lastPosition) > 0.05 ){ /* using simple raycaster throttle */

          const target = this.checkIntersects( this.interaction.position );

          document.body.style.cursor = ( target) ? 'pointer' : 'default';

          if( target && target != this.interaction.target ){

            if(this.interaction.target) this.interaction.target.onOut(); // previous target
            this.interaction.target = target;
            this.interaction.target.onOver(); // new target
            this.needsUpdate = true;

          }else if( !target && this.interaction.target ) {

            this.interaction.target.onOut(); // previous target
            this.interaction.target = null;
            this.needsUpdate = true;

          }

        }

        /* ZOOM CAMERA IF SPACE BAR IS PRESSED OR MOUSE CLICK */
        if( this.actions.zoom && this.actions.zoom != this.actions.zoomed ){
          this.stage.toggleProps( false )
          this.zoomCamera();
          this.actions.zoomed = true;
          this.needsUpdate = true;
        }else if( !this.actions.zoom && this.actions.zoom != this.actions.zoomed ){
          this.controls.reset();
          this.stage.toggleProps( true )
          this.actions.zoomed = false;
          this.needsUpdate = true;
        }

        /* MOVE ROAD ON `W` and `S` KEY AND ALSO MOVE TERRAIN TEXTURE IF ROAD MOVES */
        // if( this.actions.up  ){
        //   this.stage.move( this.delta, ( this.actions.zoomed ) ? 60 : 80 );
        //   if( this.stage.moving ) this.scenery.floorcloth.update( this.delta );
        //   this.needsUpdate = true;
        // }else if( this.actions.down ){
        //   this.stage.move( -this.delta, ( this.actions.zoomed ) ? 60 : 80 );
        //   if( this.stage.moving ) this.scenery.floorcloth.update( -this.delta );
        //   this.needsUpdate = true;
        // }

        if( this.actions.up  || this.actions.down ){

          
          const movement = ( this.actions.down ) ? -this.delta : this.delta;
          const amount = ( this.actions.zoomed ) ? 40 : 60;

          this.stage.move(movement, amount);

          if( this.stage.moving ) this.scenery.floorcloth.update( movement, amount );
          this.needsUpdate = true;


        }

        /* REMOVE CLICKED BRICK WHEN MOVING */
        if( this.interaction.active && ( this.actions.up || this.actions.down || ( !this.actions.zoom && this.actions.zoom != this.actions.zoomed ) )  ) {

          this.interaction.active.onDeselect();
          this.$emit( 'event' , { // brick or prop for now
            name : this.interaction.active.name,
            type : 'deselect',
          });
          this.interaction.active = null;
          this.needUpdate = true;
          
        } // HACK: PLEASE DON'T DELETE THIS FOR NOW!!!!!!!!!!!!

        /* KEEP ROAD ON SLOPE */
        if ( this.actions.up || this.actions.down || this.renderer.info.render.frame < 10 ){
          this.applyGravity();
          // this.needsUpdate = true;
        }
        
      },

      render(){
        if( !this.needsUpdate ) return null; /* Throttle render based on if anything has actually changed */
        this.renderer.render(this.scene, this.camera);
        this.renderer.shadowMap.needsUpdate = true;
        this.needsUpdate = false;
      },

      resizeCanvas () {
        console.log('resize!');
        this.camera.aspect = window.innerWidth / window.innerHeight;
        this.camera.updateProjectionMatrix();
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.needsUpdate = true;
      }
    }
  }
</script>